import { useAuthContext } from "contexts/AuthContext";
import FoodFetcher from "data/FoodFetcher";
import _ from "lodash";
import moment from "moment";
import React, { useState } from "react";
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import { Message, Table, Label, Loader, Dimmer, Icon } from 'semantic-ui-react'
import useSWR from "swr";
import type { Fetched } from "types";

import { sortReducer } from "../../../../utils/SortReducer";
import { sortDirection } from "../../../../utils/sortDirection";

/**
 * One member feedback row from `/food/feedback?patientId=…`, carrying the fields
 * the table renders.
 *
 * `liked` is a three-state: `true` renders a thumbs-up, `false` a thumbs-down,
 * and absent renders neither — line 134 tests all three explicitly.
 */
type FoodFeedback = {
    id?: string;
    orderId?: string;
    vendorId?: string;
    itemId?: string;
    lastUpdated?: string;
    liked?: boolean;
    rating?: number;
    feedbackText?: string;
};

/**
 * What the member-feedback table accepts.
 *
 * Established from its only call site, `../index.tsx:486`. `patientId` builds the
 * fetch URL; `selectedOrg` and `vendor` are passed by that caller and never read
 * here.
 */
type FeedbackProps = {
    /** Scopes the feedback query to one member. */
    patientId?: string | null | undefined;
    /** Accepted but unread. */
    selectedOrg?: string | undefined;
    /** Accepted but unread. */
    vendor?: string | undefined;
};

function Feedback(props: FeedbackProps) {
    const authInfo = useAuthContext();
  const [_tableData, _setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [_statusvalue, _setStatusValue] = useState('');
  const [_dateValue, _setDateValue] = useState('');
  const [_urls, _setUrls] = useState([]);
  const [_dateQuery, _setDateQuery] = useState('')
  const [_searchId, _setSearchId] = useState('');
  const [_referralTableData, _setReferralTableData] = useState([]);

    const options = {
        shouldRetryOnError: false, revalidateOnFocus: false, onSuccess:
            (data: Fetched<FoodFeedback[]>) => {
                console.log("props.patientId: ",props.patientId);
                setLoading(false);
                dispatch({type: 'CLEAR_FILTERS', data: data});
            }
    };

    /*
     * Unused: underscore-prefixed, never passed to a `useSWR` call, and its
     * `ordersFeedbackData` accumulator is built and dropped. Typed, not deleted.
     *
     * The accumulator buckets feedback rows by `orderId` then `itemId`; both key
     * reads are computed, so both records are open.
     */
    const _feedbackOptions = {
        shouldRetryOnError: false, revalidateOnFocus: false, onSuccess:
            (data: Fetched<FoodFeedback[]>) => {

             const ordersFeedbackData: Record<string, Record<string, FoodFeedback>> = {};
              if(data && data.length){

                data.forEach((order: FoodFeedback)=>{
                    if(Object.prototype.hasOwnProperty.call(ordersFeedbackData, order.orderId!)){
                        ordersFeedbackData[order.orderId!]![order.itemId!] = order;
                    }else{
                        ordersFeedbackData[order.orderId!] = {}
                        ordersFeedbackData[order.orderId!]![order.itemId!] = order;
                    }
                })
              }

            }
    };

    const { data: feedbackData, error: feedbackDataError, isValidating: feedbackDataLoading } = useSWR<Fetched<FoodFeedback[]>, Error>(
        [`/food/feedback?patientId=`+props.patientId+`&sort_desc=lastUpdated`, authInfo.token],
        FoodFetcher,
        options
    );
    
    const _resetFilters = () => {
        // setdateState('')
        // setStartRange('')
        // setEndRange('')
        // setSearchId('')
        dispatch({type: 'CLEAR_FILTERS', data: feedbackData && feedbackData})
    }

    const [state, dispatch] = React.useReducer(sortReducer as (s: unknown, a: unknown) => unknown, {
        column: null,
        data: feedbackData,
        direction: null,
      })
    const { column, data, direction } = state as {
      column: string | null;
      data: FoodFeedback[] | undefined;
      direction: 'ascending' | 'descending' | null;
    }

    return (
        <div>
          <div style={{display: 'flex', justifyContent:'space-between'}}>
            
          </div>
          {(feedbackDataLoading) && <Loader active inline="centered" />}
          {
              !feedbackData &&
              <Message warning
                header="No feedback generated yet from this member!!!"
              />
          }
          {
            feedbackDataError &&
            <Message negative>
              <Message.Header>Oops!. Something went wrong.. </Message.Header>
              <p>{feedbackDataError.message}</p>
            </Message>
          }
          <div className="table-div">
            {!feedbackDataLoading && !feedbackDataError &&
              <Table sortable celled padded singleLine>
                <Dimmer inverted active={loading}>
                  <Loader />
                </Dimmer>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell
                      width={1}
                      {...sortDirection(column === 'orderId' ? direction : null)}
                      onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'referralId' })}
                    >
                      Order Id
                    </Table.HeaderCell>
                    <Table.HeaderCell>Vendor Id</Table.HeaderCell>
                    <Table.HeaderCell
                      width={1}
                      {...sortDirection(column === 'lastUpdated' ? direction : null)}
                      onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'lastUpdated' })}
                    >
                      Feedback Date
                    </Table.HeaderCell>
                    <Table.HeaderCell>Food Item</Table.HeaderCell>
                        <Table.HeaderCell>Liked</Table.HeaderCell>
                        <Table.HeaderCell>Rating</Table.HeaderCell>
                        <Table.HeaderCell>Comment</Table.HeaderCell>
                        <Table.HeaderCell></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {/*
                    * SUSPECTED DEFECT (the line below) — REPORTED, NOT FIXED.
                    * The guard tests `feedbackData` but the map runs over `data`,
                    * the reducer's copy. Those diverge: `data` is seeded from
                    * `feedbackData` at first render, when it is still `undefined`,
                    * and is only refilled by the `CLEAR_FILTERS` dispatch inside
                    * `onSuccess`. If the fetch resolves without `onSuccess` firing
                    * — SWR 0.5.7 skips it on a cache hit — `feedbackData` is
                    * present while `data` is still `undefined`, and `.map` throws.
                    * The non-null assertion records that dependency rather than
                    * adding a `?.`, which would silently render an empty table.
                    */}
                  {feedbackData && data!.map((feedback: FoodFeedback) => (
                    <Table.Row key={feedback.id} singleLine>
                      <Table.Cell>
                        <Label color="brown">{feedback.orderId}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        {feedback.vendorId}
                      </Table.Cell>
                      <Table.Cell>{moment.utc(feedback.lastUpdated).local().format('YYYY-MM-DD hh:mm a')}</Table.Cell>
                      <Table.Cell>{feedback.itemId}</Table.Cell>
                      <Table.Cell>{feedback.liked && feedback.liked===true ?
                       (<Icon name="thumbs up" color="yellow" link size='large' />):(feedback.liked===false? <Icon name="thumbs down" color="black" link size='large' /> :"") }
                      </Table.Cell>
                      <Table.Cell>{feedback.rating}</Table.Cell>
                      <Table.Cell>{feedback.feedbackText}</Table.Cell>
                      
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            }
          </div>
        </div >
      );

};
export default Feedback;
